import ExcelJS from 'exceljs';

export interface ReportExcelHeaderOptions {
  reportTitle?: string;
  siteName: string;
  siteAddress?: string | null;
  siteTaxId?: string | null;
  startDate: string;
  endDate: string;
  extraLines?: string[];
  sheetName?: string;
}

export interface ReportExcelDownloadOptions extends ReportExcelHeaderOptions {
  fileName: string;
  sheetName?: string;
}

/** yyyy-MM-dd → dd/MM/yyyy */
export function formatReportInputDate(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return value;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

export function formatReportDateRange(startDate: string, endDate: string): string {
  return `${formatReportInputDate(startDate)} - ${formatReportInputDate(endDate)}`;
}

export function buildReportExcelFilename(
  reportCode: string,
  siteName: string,
  startDate: string,
  endDate: string,
): string {
  const safeSite = (siteName || 'site').replace(/\s+/g, '_');
  return `${reportCode}_${safeSite}_${startDate}_${endDate}.xlsx`;
}

export function displayReportSiteField(value: string | null | undefined): string {
  const trimmed = (value ?? '').trim();
  return trimmed || '-';
}

const HEADER_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFF59E0B' },
};
const TITLE_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FF1E293B' },
};
const META_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFF8FAFC' },
};
const ALT_ROW_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFFFFBEB' },
};
const THIN_BORDER: Partial<ExcelJS.Borders> = {
  top: { style: 'thin', color: { argb: 'FFCBD5E1' } },
  left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
  bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } },
  right: { style: 'thin', color: { argb: 'FFCBD5E1' } },
};

function applyFont(cell: ExcelJS.Cell, opts: Partial<ExcelJS.Font>): void {
  cell.font = { name: 'Calibri', size: 11, ...opts };
}

function styleMetaRow(row: ExcelJS.Row, colCount: number): void {
  row.height = 18;
  for (let c = 1; c <= colCount; c++) {
    const cell = row.getCell(c);
    cell.fill = META_FILL;
    applyFont(cell, { size: 10, color: { argb: 'FF334155' } });
    cell.border = THIN_BORDER;
  }
}

/**
 * สร้าง worksheet รายงานพร้อมหัวข้อ / ที่อยู่ / เลขผู้เสียภาษี และจัดรูปแบบตาราง
 * (คงชื่อเดิมเพื่อไม่กระทบ import ของหน้าเดิม — คืนค่าเป็น ExcelJS.Worksheet)
 */
export async function createReportExcelWorksheet(
  dataRows: Record<string, string | number>[],
  options: ReportExcelHeaderOptions,
): Promise<ExcelJS.Worksheet> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'City Parking Admin';
  workbook.created = new Date();

  const worksheet = fillReportWorksheet(workbook, dataRows, options);

  // stash workbook on worksheet for download helper
  (worksheet as ExcelJS.Worksheet & { __workbook?: ExcelJS.Workbook }).__workbook = workbook;
  return worksheet;
}

function fillReportWorksheet(
  workbook: ExcelJS.Workbook,
  dataRows: Record<string, string | number>[],
  options: ReportExcelHeaderOptions,
): ExcelJS.Worksheet {
  const sheetName = (options.sheetName || 'Report').slice(0, 31);
  const worksheet = workbook.addWorksheet(sheetName, {
    views: [{ state: 'frozen', ySplit: 0 }],
  });

  const keys = dataRows.length > 0
    ? Object.keys(dataRows[0])
    : ['(ไม่มีข้อมูล)'];
  const colCount = Math.max(keys.length, 1);

  worksheet.columns = keys.map((key) => ({
    key,
    width: Math.min(36, Math.max(12, key.length + 4)),
  }));

  let rowIndex = 1;

  if (options.reportTitle?.trim()) {
    const titleRow = worksheet.getRow(rowIndex);
    titleRow.getCell(1).value = options.reportTitle.trim();
    worksheet.mergeCells(rowIndex, 1, rowIndex, colCount);
    titleRow.height = 28;
    for (let c = 1; c <= colCount; c++) {
      const cell = titleRow.getCell(c);
      cell.fill = TITLE_FILL;
      applyFont(cell, { bold: true, size: 14, color: { argb: 'FFFFFFFF' } });
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
      cell.border = THIN_BORDER;
    }
    rowIndex++;
  }

  const metaLines = [
    `สถานที่: ${displayReportSiteField(options.siteName)}`,
    `ที่อยู่: ${displayReportSiteField(options.siteAddress)}`,
    `เลขประจำตัวผู้เสียภาษี: ${displayReportSiteField(options.siteTaxId)}`,
    `ช่วงวันที่: ${formatReportDateRange(options.startDate, options.endDate)}`,
    ...(options.extraLines ?? []).filter((l) => l.trim()).map((l) => l.trim()),
  ];

  for (const line of metaLines) {
    const metaRow = worksheet.getRow(rowIndex);
    metaRow.getCell(1).value = line;
    worksheet.mergeCells(rowIndex, 1, rowIndex, colCount);
    styleMetaRow(metaRow, colCount);
    rowIndex++;
  }

  // spacer
  rowIndex++;

  const headerRowIndex = rowIndex;
  const headerRow = worksheet.getRow(headerRowIndex);
  keys.forEach((key, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = key;
    cell.fill = HEADER_FILL;
    applyFont(cell, { bold: true, size: 10, color: { argb: 'FF1E293B' } });
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = THIN_BORDER;
  });
  headerRow.height = 22;
  rowIndex++;

  dataRows.forEach((data, idx) => {
    const row = worksheet.getRow(rowIndex);
    keys.forEach((key, i) => {
      const cell = row.getCell(i + 1);
      const value = data[key];
      cell.value = value ?? '';
      applyFont(cell, { size: 10 });
      cell.border = THIN_BORDER;
      cell.alignment = { vertical: 'middle' };
      if (idx % 2 === 1) {
        cell.fill = ALT_ROW_FILL;
      }
      if (typeof value === 'number') {
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
        cell.numFmt = Number.isInteger(value) ? '#,##0' : '#,##0.00';
      }
    });
    row.height = 18;
    rowIndex++;
  });

  // auto-ish widths from content
  worksheet.columns.forEach((col, i) => {
    const key = keys[i] ?? '';
    let max = key.length;
    for (const data of dataRows.slice(0, 80)) {
      const v = data[key];
      const len = v == null ? 0 : String(v).length;
      if (len > max) max = len;
    }
    col.width = Math.min(42, Math.max(12, max + 2));
  });

  worksheet.views = [{ state: 'frozen', ySplit: headerRowIndex }];
  return worksheet;
}

/** ส่งออก Excel รายงานพร้อมจัดรูปแบบ (ใช้แทน book_new + writeFile ของ sheetjs) */
export async function downloadReportExcel(
  dataRows: Record<string, string | number>[],
  options: ReportExcelDownloadOptions,
): Promise<void> {
  const worksheet = await createReportExcelWorksheet(dataRows, options);
  const workbook =
    (worksheet as ExcelJS.Worksheet & { __workbook?: ExcelJS.Workbook }).__workbook
    ?? worksheet.workbook;

  await saveReportWorkbook(workbook, options.fileName);
}

export interface ReportExcelSheet {
  sheetName: string;
  dataRows: Record<string, string | number>[];
  /** ต่อท้าย meta ของ options (เฉพาะชีตนี้) */
  extraLines?: string[];
}

/** ส่งออก Excel หลายชีต — ทุกชีตมีหัวรายงาน / ที่อยู่ / เลขผู้เสียภาษีเหมือนกัน */
export async function downloadReportExcelWorkbook(
  sheets: ReportExcelSheet[],
  options: Omit<ReportExcelDownloadOptions, 'sheetName'>,
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'City Parking Admin';
  workbook.created = new Date();

  for (const sheet of sheets) {
    fillReportWorksheet(workbook, sheet.dataRows, {
      ...options,
      sheetName: sheet.sheetName,
      extraLines: [...(options.extraLines ?? []), ...(sheet.extraLines ?? [])],
    });
  }

  await saveReportWorkbook(workbook, options.fileName);
}

async function saveReportWorkbook(workbook: ExcelJS.Workbook, fileName: string): Promise<void> {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
